import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    Input,
    ListItemText,
    Radio,
    RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { ActiveDeactivateType, ActiveDeactiveResult } from 'hotfile-api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import services from 'services';
import styled from 'styled-components/macro';
import chunkIt from 'utils/chunkIt';
import FileUpload from './FileUpload';
import { LinearProgressWithLabel } from './LinearProgressWithLabel';

type ActionStatuses = { [key: string]: ActiveDeactiveResult } | undefined;

type Upload = {
    inProgress: boolean;
    count?: number;
    total?: number;
};

const SwissArmyUpload = () => {
    const { instance, inProgress } = useMsal();
    const { t } = useTranslation();
    const fileInput = useRef<HTMLInputElement>(null);
    const [files, setFiles] = useState<File[]>([]);
    const [items, setItems] = useState<string[]>([]);
    const [actionStatuses, setActionStatus] = useState<ActionStatuses>({});
    const [operationType, setOperationType] = useState<boolean>(true);
    const [operationValue, setOperationValue] = useState<ActiveDeactivateType>(ActiveDeactivateType.Company);
    const [operationKey, setOperationKey] = useState<string | undefined | null>();
    const [readingFile, setReadingFile] = useState<boolean>(false);
    const [upload, setUpload] = useState<Upload>({
        inProgress: false,
        count: 0,
        total: 0,
    });

    useEffect(() => {
        console.log('RUNNING', files);
        if (files && files.length > 0 && inProgress === InteractionStatus.None) {
            setReadingFile(true);
            services.swissarmy
                .upload({ files: files })
                .then((response: any) => {
                    setActionStatus({});
                    setItems((prev) => [...new Set([...(prev ?? [])].concat(response))]);
                    setReadingFile(false);
                    setFiles([]);
                })
                .finally(() => {
                    setReadingFile(false);
                });
        }

        if (!fileInput.current) {
            return;
        }

        const dataTransfer = new DataTransfer();
        files.map((file) => dataTransfer.items.add(file));
        fileInput.current.files = dataTransfer.files;
    }, [files, instance, inProgress, operationValue, items]);

    const uploadFiles = useCallback(async (files: FileList) => {
        if (!(files.length > 0)) {
            return;
        }

        await setFiles([...files]);
    }, []);

    const performAction = useCallback(async () => {
        const itemsInChunks = chunkIt(items ?? [], 20);

        console.log(operationKey);
        console.log(operationValue);
        console.log(operationType);

        setActionStatus({});
        await setUpload((prev) => ({
            ...prev,
            inProgress: true,
            total: itemsInChunks.length + 1,
            count: 0,
        }));

        Promise.all(
            itemsInChunks.map(async (chunkedItem: string[]) => {
                return services.swissarmy
                    .activateInactive({
                        items: chunkedItem,
                        type: operationValue,
                        key: operationKey,
                        active: operationType,
                    })
                    .then(async (response) => {
                        setActionStatus(
                            (prev) =>
                                ({
                                    ...prev,
                                    ...response.reduce(
                                        (acc, item: ActiveDeactiveResult) => ({
                                            ...acc,
                                            [`${item.item}`]: item,
                                        }),
                                        {},
                                    ),
                                } as ActionStatuses),
                        );

                        await setUpload((prev) => ({
                            ...prev,
                            count: prev.count! < prev.total! ? prev.count! + 1 : prev.count,
                        }));
                    })
                    .catch(async () => {
                        await setUpload((prev) => ({
                            ...prev,
                            count: prev.count! < prev.total! ? prev.count! + 1 : prev.count,
                        }));
                    });
            }),
        ).finally(async () => {
            await setUpload((prev) => ({
                ...prev,
                count: prev.count! + 1,
            }));

            await setTimeout(() => {
                setUpload((prev) => ({ ...prev, inProgress: false }));
            }, 1000);
        });
    }, [items, operationValue, operationKey, operationType]);

    const operationValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        switch (event.target.value) {
            case 'company':
                setOperationValue(ActiveDeactivateType.Company);
                break;
            case 'channel':
                setOperationValue(ActiveDeactivateType.Channel);
                break;
            case 'category':
                setOperationValue(ActiveDeactivateType.Category);
                break;
            case 'assortment':
                setOperationValue(ActiveDeactivateType.Assortment);
                break;
            case 'feature':
                setOperationValue(ActiveDeactivateType.Feature);
                break;
            case 'range':
                setOperationValue(ActiveDeactivateType.Range);
                break;
            case 'season':
                setOperationValue(ActiveDeactivateType.Season);
                break;
            case 'theme':
                setOperationValue(ActiveDeactivateType.Theme);
                break;
            case 'sustainability':
                setOperationValue(ActiveDeactivateType.Sustainability);
                break;
        }
    };

    const operationTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        switch (event.target.value) {
            case 'activate':
                setOperationType(true);
                break;
            case 'deactivate':
                setOperationType(false);
                break;
        }
    };

    const getOperationType = (operationType: boolean): string => {
        switch (operationType) {
            case true:
                return 'activate';
            case false:
                return 'deactivate';
        }
    };

    const getOperationValue = (operationValue: ActiveDeactivateType): string => {
        switch (operationValue) {
            case ActiveDeactivateType.Company:
                return 'company';
            case ActiveDeactivateType.Channel:
                return 'channel';
            case ActiveDeactivateType.Category:
                return 'category';
            case ActiveDeactivateType.Assortment:
                return 'assortment';
            case ActiveDeactivateType.Feature:
                return 'feature';
            case ActiveDeactivateType.Range:
                return 'range';
            case ActiveDeactivateType.Season:
                return 'season';
            case ActiveDeactivateType.Sustainability:
                return 'sustainability';
            case ActiveDeactivateType.Theme:
                return 'theme';
        }
    };

    const getOperationValueLabel = (operationType: ActiveDeactivateType): string => {
        switch (operationType) {
            case ActiveDeactivateType.Company:
                return 'Company';
            case ActiveDeactivateType.Channel:
                return 'Channel';
            case ActiveDeactivateType.Category:
                return 'Category';
            case ActiveDeactivateType.Assortment:
                return 'Assortment';
            case ActiveDeactivateType.Feature:
                return 'Feature';
            case ActiveDeactivateType.Range:
                return 'Range';
            case ActiveDeactivateType.Season:
                return 'Season';
            case ActiveDeactivateType.Sustainability:
                return 'Sustainability';
            case ActiveDeactivateType.Theme:
                return 'Theme';
        }
    };

    return (
        <Box>
            <Box sx={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column' }}>
                <StyledFileUpload
                    fileInputRef={fileInput}
                    handleDrop={uploadFiles}
                    uploadFiles={uploadFiles}
                    getDataFromClipboardEnabled
                    clipboardImageEnabled={true}
                    multiple={true}
                    allowedFiletypes={t('upload.supportedXlsx', '.xlsx, .txt, .csv') ?? ''}
                />
                <FormControl sx={{ marginBottom: '2rem' }}>
                    <FormLabel id="demo-row-radio-buttons-group-label2">Operation</FormLabel>
                    <RadioGroup
                        row
                        aria-labelledby="demo-radio-buttons-group-label-operation"
                        name="radio-buttons-group-operation"
                        value={getOperationType(operationType)}
                        onChange={operationTypeChange}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <FormControlLabel value="activate" control={<Radio />} label="Activate" />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <FormControlLabel value="deactivate" control={<Radio />} label="Deactivate" />
                            </Box>
                        </Box>
                    </RadioGroup>
                    <FormLabel id="demo-row-radio-buttons-group-label">Value</FormLabel>
                    <RadioGroup
                        row
                        aria-labelledby="demo-radio-buttons-group-label"
                        name="radio-buttons-group"
                        value={getOperationValue(operationValue)}
                        onChange={operationValueChange}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <FormControlLabel value="company" control={<Radio />} label=" Company" />
                                <FormControlLabel value="channel" control={<Radio />} label="Channel" />
                                <FormControlLabel value="season" control={<Radio />} label="Season" />
                                <FormControlLabel value="assortment" control={<Radio />} label="Assortment" />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <FormControlLabel value="category" control={<Radio />} label="Category" />
                                <FormControlLabel value="theme" control={<Radio />} label=" Theme" />
                                <FormControlLabel value="range" control={<Radio />} label="Range" />
                                <FormControlLabel value="feature" control={<Radio />} label="Feature" />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <FormControlLabel value="sustainability" control={<Radio />} label="Sustainability" />
                            </Box>
                        </Box>
                    </RadioGroup>
                    <FormLabel sx={{ marginTop: '0.5rem' }} id="demo-row-radio-buttons-group-label">
                        {getOperationValueLabel(operationValue)}
                    </FormLabel>
                    <Input
                        size="small"
                        type="text"
                        sx={{ width: '6rem' }}
                        onChange={(event) => setOperationKey(event.target.value)}
                    ></Input>
                </FormControl>
                <Box sx={{ maxWidth: 1000, display: 'flex' }}>
                    <Button
                        disabled={
                            !(items?.length && items?.length > 0) ||
                            readingFile ||
                            operationKey === undefined ||
                            operationKey == null ||
                            operationKey.length === 0
                        }
                        variant="contained"
                        color="success"
                        size="small"
                        type="submit"
                        onClick={performAction}
                        sx={{ marginRight: 'auto' }}
                    >
                        {operationType ? t('upload.active', 'Activate') : t('upload.deactivate', 'Deactivate')}
                    </Button>
                    <Button
                        disabled={items?.length === 0 || readingFile}
                        color="error"
                        size="small"
                        type="submit"
                        startIcon={<DeleteIcon color="inherit" />}
                        onClick={() => setItems([])}
                    >
                        {t('upload.clear', 'Clear')}
                    </Button>
                </Box>
            </Box>
            <TableContainer component={Box}>
                <Table sx={{ maxWidth: 1000 }} size="small" aria-label="a dense table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '10%' }}>No.</TableCell>
                            <TableCell sx={{ width: '30%' }}>Status</TableCell>
                            <TableCell sx={{ width: '60%' }}>Product/Item</TableCell>
                            <TableCell sx={{ width: '10%' }} align="right"></TableCell>
                        </TableRow>
                    </TableHead>

                    {readingFile ? (
                        <TableBody>
                            <TableRow sx={{ border: 0 }}>
                                <TableCell colSpan={3} sx={{ border: 0, alignItems: 'center' }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    ) : items?.length === 0 ? null : (
                        <TableBody>
                            {items?.map((item, index) => (
                                <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell sx={{ width: '10%' }} scope="row">
                                        {index + 1}
                                    </TableCell>
                                    <TableCell sx={{ width: '10%' }} scope="row">
                                        {actionStatuses?.[item]?.result ?? ''}
                                        {(actionStatuses?.[item]?.items ?? 0) > 0
                                            ? ', ' + actionStatuses?.[item]?.items + ' entities'
                                            : ''}
                                    </TableCell>
                                    <TableCell sx={{ width: '60%' }}>
                                        <ListItemText key={index} sx={{ wordBreak: 'break-all' }} primary={item} />
                                    </TableCell>
                                    <TableCell sx={{ width: '10%' }} align="right">
                                        <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            size="small"
                                            color="error"
                                            onClick={() => setItems((prev) => (prev ?? []).filter((i) => item !== i))}
                                        >
                                            <DeleteIcon fontSize="inherit" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    )}
                </Table>
            </TableContainer>
            {upload.inProgress && (
                <Backdrop sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }} open={upload.inProgress}>
                    <Box sx={{ background: '#fff', borderRadius: 2, p: 2 }}>
                        <LinearProgressWithLabel inProgress={true} count={upload.count} total={upload.total} />
                    </Box>
                </Backdrop>
            )}
        </Box>
    );
};

const StyledFileUpload = styled(FileUpload)`
    padding: 1rem;
    margin-bottom: 1rem;
    width: 700px;
    height: 230px;
`;

export default SwissArmyUpload;
